<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { onClickOutside, useElementSize, useParentElement } from '@vueuse/core';
import useVuelidate from '@vuelidate/core';
import IconListItem from '../IconListItem.vue';
import { getFormattedFileSize } from '../../../utils/getFormattedFileSize';
import { useBreakpoints } from '../../../composables/breakpoint';
import AppIcon from '../../atoms/app/AppIcon.vue';
import { useApplicationStore } from '../../../stores/application-store';
import AppLink from '../../atoms/app/AppLink.vue';
import CircularProgressIndicator from '../../atoms/progress/CircularProgressIndicator.vue';
import LogoKatLetter from '../../spots/LogoKatLetter.vue';
import { track } from '../../../utils/tracking';
import { usePdfPreview } from '../../../composables/pdf-preview';
import { getHumanizedDateDifference } from '../../../utils/date';
import { validateIf } from '../../../utils/validators';
import WebOnly from '../../webview/WebOnly.vue';
import { useInjectedErrors } from '../../../composables/use-injected-errors';
import { useWebViewContext } from '../../../composables/webview-context';

const props = defineProps({
    file: {
        type: Object,
        default: undefined,
    },
    uploadProgress: {
        type: Number,
        default: undefined,
    },
    isCurrentlyUploading: {
        type: Boolean,
        default: false,
    },
    disableAttachmentActivation: {
        type: Boolean,
        default: false,
    },
    hideActions: {
        type: Boolean,
        default: false,
    },
    hideCvCreateDate: {
        type: Boolean,
        default: false,
    },
    createDateOnly: {
        type: Boolean,
        default: false,
    },
    isPreview: {
        type: Boolean,
        default: false,
    },
    smallGaps: {
        type: Boolean,
        default: false,
    },
    boldTitle: {
        type: Boolean,
        default: false,
    },
    underlineTitle: {
        type: Boolean,
        default: false,
    },
    alwaysActive: {
        type: Boolean,
        default: false,
    },
    insideExtractCvModal: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['abort-upload', 'remove-file-from-queue', 'attachment-changed', 'edit-cv']);

const { isMobile } = useBreakpoints();

const application = useApplicationStore();

const { isWebView } = useWebViewContext();

const waitingForCv = computed(() => props.file.isCv && props.file.progress !== undefined &&
    application.cvFile.isDownloading);
const isCv = computed(() => props.file.isCv);

// Cut off file name after certain px points to avoid ugly overflow behavior
// Browser-based ellipsis shows bad results depending on the file names
const attachmentTitleElement = ref(null);
const parent = useParentElement(attachmentTitleElement);
const { width } = useElementSize(parent);
const maxFileNameLengthInCharacters = computed(() => Math.max(10, Math.round(width.value / 10)));

const { cvErrors } = useInjectedErrors();

function getCvLabel() {
    if (waitingForCv.value) {
        return 'Dein Lebenslauf wird geladen...';
    } if (props.file.isRendering) {
        return 'Dein karriere.at Lebenslauf wird für diese Bewerbung aktualisiert...';
    } if (cvErrors.value.keys.includes('rendering-cv-failed') && isCv.value) {
        return 'Beim Aktualisieren von deinem karriere.at Lebenslauf ist etwas schief gelaufen.';
    } if (cvErrors.value.keys.includes('loading-cv-failed') && isCv.value) {
        return 'Dein karriere.at Lebenslauf konnte nicht geladen werden';
    } if (props.isPreview) {
        return 'Mein karriere.at Lebenslauf';
    }

    return 'Meinen karriere.at Lebenslauf verwenden';
}

function getAttachmentLabel() {
    const parts = props.file.name?.toLowerCase().split('.');

    const fileExt = parts.pop();
    let fileNameWithoutExt = parts.join('.');

    if (fileNameWithoutExt.length > maxFileNameLengthInCharacters.value) {
        fileNameWithoutExt = `${fileNameWithoutExt.substring(0, maxFileNameLengthInCharacters.value - 3)}...`;
    }
    return `${fileNameWithoutExt}.${fileExt}`;
}

const fileNameToDisplay = computed(() => {
    if (isCv.value) return getCvLabel();
    return getAttachmentLabel();
});

const isCvReady = computed(() => !waitingForCv.value &&
        !cvErrors.value.keys.includes('loading-cv-failed') &&
        !props.file.isRendering &&
        !cvErrors.value.keys.includes('rendering-cv-failed'));

const fileName = computed(() => props.file.name?.split('.').slice(0, -1).join('.')
    .replaceAll(' ', '-')
    .replaceAll('.', '')
    .toLowerCase());
const fileExtension = computed(() => `.${props.file.name?.split('.').pop()}`);
const fileSize = computed(() => getFormattedFileSize(props.file.size));
const showUploadProgress = computed(() => props.uploadProgress !== undefined);
const isDeactivated = computed(() => props.file.shouldBeAttachedToApplication === false &&
    !showUploadProgress.value && !props.isPreview);
const trackingAttachmentType = computed(() => {
    if (isCv.value) return 'k.at CV';
    if (props.file.isProfileDocument) return 'Files from profile';
    return 'Uploaded file';
});

const previewLink = computed(() => props.file.previewLink);

const activateAttachment = () => {
    if (!isCv.value) {
        application.activateAttachment(props.file.uuid);
    }
    track({
        element: `AP_F: ${trackingAttachmentType.value}`,
        elementDetail: 'Re-added',
    });
    emit('attachment-changed');
};

const deactivateAttachment = () => {
    if (!isCv.value) {
        application.deactivateAttachment(props.file.uuid);
    }
    track({
        element: `AP_F: ${trackingAttachmentType.value}`,
        elementDetail: 'Removed',
    });
    emit('attachment-changed');
};

const imageFileExtensions = ['.jpg', '.png', '.jpeg', '.gif'];
const textFileExtensions = ['.doc', '.docx', '.odt', '.rtf'];
const pdfFileExtensions = ['.pdf'];

const FILE_TYPES = {
    IMAGE: 0,
    TEXT: 1,
    PDF: 2,
    UNKNOWN: 3,
};

const fileType = computed(() => {
    if (imageFileExtensions.includes(fileExtension.value)) {
        return FILE_TYPES.IMAGE;
    } if (textFileExtensions.includes(fileExtension.value)) {
        return FILE_TYPES.TEXT;
    } if (pdfFileExtensions.includes(fileExtension.value)) {
        return FILE_TYPES.PDF;
    }
    return FILE_TYPES.UNKNOWN;
});

const createdAtTimeString = computed(() => {
    if (!isCv.value) return undefined; // only needed for cv

    return getHumanizedDateDifference(props.file.createDate);
});

function abortUpload() {
    if (props.isCurrentlyUploading) {
        emit('abort-upload');
    } else {
        emit('remove-file-from-queue', props.file.name);
    }
    track({
        element: 'AP_F: Unfinished upload aborted',
    });
}

function trackPreview() {
    track({
        element: `AP_F: ${trackingAttachmentType.value}`,
        elementDetail: 'Previewed',
    });
}

/* CV Edit */
function editCv() {
    emit('edit-cv');

    track({
        element: 'AP_F: k.at CV',
        elementDetail: 'Edited',
    });
}

/* Mobile options dropdown */
const optionsDropdown = ref(null);
const dropdownDots = ref(null);
const dropdownActive = ref(false);
const toggleDropdown = () => {
    dropdownActive.value = !dropdownActive.value;
    track({
        element: 'AP_F: Action menu click without action',
    });
};
onClickOutside(optionsDropdown, () => { dropdownActive.value = false; }, { ignore: [dropdownDots] });

/* PDF preview generation */
const canvas = ref(null);
const { generatePdfPreview, pdfPreviewAvailable, currentlyGeneratingPreview } = usePdfPreview();

onMounted(() => watch(() => props.file, (file) => {
    if (!file.previewLink || !(fileType.value === FILE_TYPES.PDF)) return;
    // Make sure there are no preview generations currently running
    if (currentlyGeneratingPreview.value) return;

    // A CV used for extraction does not need a preview inside the modal
    if (props.insideExtractCvModal) return;

    generatePdfPreview({
        fileUrl: file.previewLink,
        canvas,
        fileOrigin: trackingAttachmentType.value,
    });
}, { immediate: true, deep: true }));

/* Image preview - show fallback if img can't be loaded */
const showImageFallback = ref(false);
const onImageLoadError = () => { showImageFallback.value = true; };

/* Validation - active files may not be gifs */
const file = computed(() => props.file);

const validations = {
    file: {
        activeFileMayNotBeGif: validateIf(
            () => file.value.shouldBeAttachedToApplication,
            fileToValidate => fileToValidate.mimetype !== 'image/gif',
        ),
    },
};

const v$ = useVuelidate(validations, { file });
</script>

<template>
    <IconListItem
        :class="{
            'has-status-error': v$.$invalid,
        }"
        :muted="isDeactivated"
        :small-gaps="smallGaps"
        :align-actions-top="isCv"
        :error="v$.$invalid || (cvErrors.keys.includes('rendering-cv-failed') && isCv)"
    >
        <template #icon>
            <template v-if="showUploadProgress">
                <div :class="waitingForCv && 'animate-spin'">
                    <CircularProgressIndicator
                        class="c-fileListEntry__progressIndicator"
                        data-qa="upload progress circle"
                        :progress="uploadProgress"
                    />
                </div>
            </template>
            <template v-else-if="v$.$invalid">
                <AppIcon
                    name="alert"
                    class="text-error-text"
                    size="xl"
                />
            </template>
            <template v-else>
                <AppLink
                    neutral
                    class="h-full w-full flex items-center justify-center"
                    :tag="waitingForCv ? 'div' : 'a'"
                    :href="previewLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    tabindex="-1"
                    aria-label="Lebenslauf Vorschau öffnen"
                    @click="trackPreview"
                >
                    <template v-if="fileType === FILE_TYPES.IMAGE">
                        <img
                            v-if="file.previewLink && !showImageFallback"
                            :src="previewLink"
                            class="object-cover w-full h-full"
                            alt=""
                            @error="onImageLoadError"
                        >
                        <AppIcon
                            v-else
                            name="picture-stack"
                            size="xl"
                        />
                    </template>
                    <template v-else-if="fileType === FILE_TYPES.TEXT">
                        <AppIcon
                            name="file-text"
                            size="xl"
                        />
                    </template>
                    <template v-else-if="fileType === FILE_TYPES.PDF">
                        <canvas
                            v-show="pdfPreviewAvailable"
                            ref="canvas"
                            class="c-fileListEntry__pdfPreview"
                        />
                        <AppIcon
                            v-if="!pdfPreviewAvailable"
                            name="file-pdf"
                            size="xl"
                        />
                        <LogoKatLetter
                            v-if="isCv"
                            class="c-fileListEntry__katLogo"
                        />
                    </template>
                    <template v-else-if="fileType === FILE_TYPES.UNKNOWN">
                        <AppIcon
                            name="file"
                            size="xl"
                        />
                    </template>
                </AppLink>
            </template>
        </template>
        <template #title>
            <div
                ref="attachmentTitleElement"
                class="flex"
                :class="{
                    'font-bold': boldTitle || v$.$invalid,
                    'text-error-text': v$.$invalid,
                    underline: underlineTitle,
                }"
            >
                {{ fileNameToDisplay }}
            </div>
        </template>
        <template
            v-if="!isCv || !showUploadProgress"
            #subtitle
        >
            <template v-if="v$.$invalid">
                <span class="text-error-text">Dieser Dateityp wird nicht mehr unterstützt.</span>
            </template>
            <div
                v-if="isCvReady"
                :class="insideExtractCvModal && 'text-left'"
            >
                {{ fileSize }}
                <template v-if="isCv">
                    <template v-if="createDateOnly">
                        ・ {{ createdAtTimeString }}
                    </template>
                    <template v-else-if="!hideCvCreateDate">
                        ・ Erstellt {{ createdAtTimeString }}
                    </template>
                </template>
            </div>
        </template>
        <template
            v-if="!hideActions"
            #actions
        >
            <WebOnly v-if="isPreview">
                <AppLink
                    v-if="isCvReady"
                    neutral
                    class="c-fileListEntry__action"
                    :aria-label="`${fileName} in neuem Tab betrachten`"
                    :href="previewLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-qa="preview link"
                    @click="trackPreview"
                >
                    <AppIcon
                        name="preview"
                        size="xl"
                    />
                </AppLink>
            </WebOnly>
            <template v-else-if="isMobile">
                <template v-if="isDeactivated && !isCv">
                    <AppLink
                        tag="button"
                        neutral
                        class="c-fileListEntry__action"
                        type="button"
                        :aria-label="`${fileName} zu Anhängen hinzufügen`"
                        :disabled="disableAttachmentActivation"
                        @click="activateAttachment"
                    >
                        <AppIcon
                            name="plus"
                            size="xl"
                        />
                    </AppLink>
                </template>
                <template v-else>
                    <template v-if="showUploadProgress && !isCv">
                        <AppLink
                            tag="button"
                            neutral
                            class="c-fileListEntry__action"
                            :aria-label="`Upload von ${fileName} abbrechen`"
                            type="button"
                            @click="abortUpload"
                        >
                            <AppIcon
                                name="cross"
                                size="xl"
                            />
                        </AppLink>
                    </template>
                    <template v-else>
                        <AppLink
                            v-if="!isWebView || isWebView && !isCv"
                            ref="dropdownDots"
                            tag="button"
                            neutral
                            class="c-fileListEntry__action"
                            data-qa="mobile attachment action dropdown toggle"
                            :aria-label="`Optionen für ${fileName} als Dropdown öffnen`"
                            type="button"
                            @click="toggleDropdown"
                        >
                            <AppIcon
                                class="rotate-90"
                                name="dots"
                                size="l"
                            />
                        </AppLink>
                        <ul
                            v-if="dropdownActive"
                            ref="optionsDropdown"
                            class="c-fileListEntry__dropdown"
                        >
                            <WebOnly>
                                <AppLink
                                    neutral
                                    class="c-fileListEntry__dropdownItem k-c-dropdown__listItem p-m hover:no-underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :aria-label="`${fileName} in neuem Tab betrachten`"
                                    data-qa="preview link"
                                    :href="previewLink"
                                    @click="trackPreview"
                                >
                                    Vorschau
                                </AppLink>
                            </WebOnly>
                            <WebOnly>
                                <AppLink
                                    v-if="isCv"
                                    neutral
                                    tag="button"
                                    aria-label="Deinen Lebenslauf bearbeiten"
                                    class="c-fileListEntry__dropdownItem k-c-dropdown__listItem
                                       p-m hover:no-underline text-left"
                                    data-qa="edit cv"
                                    @click="editCv"
                                >
                                    Bearbeiten
                                </AppLink>
                            </WebOnly>
                            <AppLink
                                v-if="!isCv"
                                neutral
                                tag="button"
                                class="c-fileListEntry__dropdownItem k-c-dropdown__listItem
                                       p-m hover:no-underline text-left"
                                :aria-label="`${fileName} aus Anhängen entfernen`"
                                type="button"
                                @click="deactivateAttachment"
                            >
                                Entfernen
                            </AppLink>
                        </ul>
                    </template>
                </template>
            </template>
            <template v-else>
                <div class="c-fileListEntry__actions">
                    <template v-if="isDeactivated">
                        <AppLink
                            tag="button"
                            class="c-fileListEntry__action"
                            :aria-label="`${fileName} zu den Anhängen hinzufügen`"
                            type="button"
                            :disabled="disableAttachmentActivation"
                            @click="activateAttachment"
                        >
                            Hinzufügen
                        </AppLink>
                    </template>
                    <template v-else-if="!showUploadProgress">
                        <AppLink
                            v-if="!waitingForCv"
                            neutral
                            class="c-fileListEntry__action"
                            :aria-label="`${fileName} in neuem Tab betrachten`"
                            :href="previewLink"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-qa="preview link"
                            @click="trackPreview"
                        >
                            <AppIcon
                                name="preview"
                                size="xl"
                            />
                        </AppLink>
                        <AppLink
                            v-if="isCv"
                            neutral
                            class="c-fileListEntry__action"
                            tag="button"
                            aria-label="Deinen Lebenslauf bearbeiten"
                            data-qa="edit cv"
                            @click="editCv"
                        >
                            <AppIcon
                                name="pen"
                                size="xl"
                            />
                        </AppLink>
                        <AppLink
                            v-if="!isCv"
                            tag="button"
                            neutral
                            class="c-fileListEntry__action"
                            :aria-label="`${fileName} aus Anhängen entfernen`"
                            type="button"
                            @click="deactivateAttachment"
                        >
                            <AppIcon
                                name="cross"
                                size="xl"
                            />
                        </AppLink>
                    </template>
                    <template v-else-if="showUploadProgress && !isCv">
                        <AppLink
                            tag="button"
                            neutral
                            class="c-fileListEntry__action"
                            :aria-label="`Upload von ${fileName} abbrechen`"
                            type="button"
                            @click="abortUpload"
                        >
                            <AppIcon
                                name="cross"
                                size="xl"
                            />
                        </AppLink>
                    </template>
                </div>
            </template>
        </template>
    </IconListItem>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/components/dropdown';
@use '~@karriereat/global-styles/scss/objects/chain';
@use '~@karriereat/global-styles/scss/components/link/settings' as link;
@use '~@karriereat/global-styles/scss/components/list/index' as list;
@use '../../../utils/reset';

$progress-indicator-scaling: 1.4;
$dropdown-width: 8rem;
$pdf-preview-offset: translate(spacing.$k-spacing--s, spacing.$k-spacing--s);
$pdf-background-color: var(--k-white);

.c-fileListEntry {
    &__progressIndicator {
        scale: $progress-indicator-scaling;
    }

    &__actions {
        position: relative;
        display: flex;
        gap: spacing.$k-spacing--xl;
    }

    &__action {
        display: flex;
        align-items: center;
        @include reset.buttonReset();
    }

    &__dropdown {
        @extend .k-c-dropdown__list;
        @extend .k-c-dropdown__list--open;
        right: 0;
        z-index: 1;
        width: $dropdown-width;
        border-top: 1px solid var(--k-gray-300);
    }

    &__dropdownItem {
        @include reset.buttonReset();
        display: block;
        width: 100%;
    }

    &__katLogo {
        position: absolute;
        right: spacing.$k-spacing--xs;
        bottom: spacing.$k-spacing--xs;
        width: 1rem;
        height: 1rem;
    }

    &__pdfPreview {
        position: absolute;
        top: 0;
        left: 0;
        background-color: $pdf-background-color;
        transform: $pdf-preview-offset;
    }
}
</style>
